import { supabase } from '@/integrations/supabase/client';
import { withServiceErrorHandling } from '@/lib/service-error';
import { logger } from '@/lib/structured-logger';
import type { Database } from '@/integrations/supabase/types';

type NewsletterCampaign = Database['public']['Tables']['newsletter_campaigns']['Row'];
type NewsletterCampaignInsert = Database['public']['Tables']['newsletter_campaigns']['Insert'];
type NewsletterCampaignUpdate = Database['public']['Tables']['newsletter_campaigns']['Update'];

export interface CampaignAnalytics {
  campaignId: string;
  title: string;
  subject: string;
  status: string;
  campaignType: string;
  sentAt: string | null;
  recipientCount: number;
  openCount: number;
  clickCount: number;
  openRate: number;
  clickRate: number;
  clickToOpenRate: number;
}

export interface CampaignComparison {
  campaigns: CampaignAnalytics[];
  averageOpenRate: number;
  averageClickRate: number;
  bestPerforming: CampaignAnalytics | null;
  worstPerforming: CampaignAnalytics | null;
}

export class NewsletterService {
  private static readonly TABLE = 'newsletter_campaigns';
  private static readonly logger = logger.child({ service: 'NewsletterService' });

  /**
   * Get all newsletter campaigns with pagination
   */
  static async getAll(limit: number = 100, offset: number = 0) {
    return withServiceErrorHandling(async () => {
      this.logger.debug('Fetching newsletters', { limit, offset });
      const { data, error } = await supabase
        .from(this.TABLE)
        .select('*', { count: 'exact' })
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);

      if (error) throw error;

      this.logger.info('Newsletters fetched', { count: data?.length || 0 });
      return data || [];
    }, { operation: 'getAll', table: this.TABLE });
  }

  /**
   * Get a single campaign by ID
   */
  static async getById(id: string) {
    const { data, error } = await supabase
      .from('newsletter_campaigns')
      .select('*')
      .eq('id', id)
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to fetch campaign: ${error.message}`);
    }

    return data;
  }

  /**
   * Create a new newsletter campaign
   */
  static async create(campaign: NewsletterCampaignInsert) {
    const { data, error } = await supabase
      .from('newsletter_campaigns')
      .insert(campaign)
      .select()
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to create campaign: ${error.message}`);
    }

    return data;
  }

  /**
   * Update a campaign
   */
  static async update(id: string, updates: NewsletterCampaignUpdate) {
    const { data, error } = await supabase
      .from('newsletter_campaigns')
      .update(updates)
      .eq('id', id)
      .select()
      .maybeSingle();

    if (error) {
      throw new Error(`Failed to update campaign: ${error.message}`);
    }

    return data;
  }

  /**
   * Delete a campaign (only drafts)
   */
  static async delete(id: string) {
    const { error } = await supabase
      .from('newsletter_campaigns')
      .delete()
      .eq('id', id)
      .eq('status', 'draft');

    if (error) {
      throw new Error(`Failed to delete campaign: ${error.message}`);
    }
  }

  /**
   * Get campaign analytics with calculated rates
   */
  static async getCampaignAnalytics(campaignId: string): Promise<CampaignAnalytics | null> {
    const campaign = await this.getById(campaignId);
    
    if (!campaign) {
      return null;
    }

    return this.calculateAnalytics(campaign);
  }

  /**
   * Get analytics for all sent campaigns
   */
  static async getAllCampaignAnalytics(): Promise<CampaignAnalytics[]> {
    const campaigns = await this.getAll();
    const sentCampaigns = campaigns.filter(c => c.status === 'sent');
    
    return sentCampaigns.map(campaign => this.calculateAnalytics(campaign));
  }

  /**
   * Compare multiple campaigns
   */
  static async compareCampaigns(campaignIds?: string[]): Promise<CampaignComparison> {
    let campaigns: NewsletterCampaign[];

    if (campaignIds && campaignIds.length > 0) {
      const { data, error } = await supabase
        .from('newsletter_campaigns')
        .select('*')
        .in('id', campaignIds)
        .eq('status', 'sent');

      if (error) {
        throw new Error(`Failed to fetch campaigns for comparison: ${error.message}`);
      }

      campaigns = data || [];
    } else {
      campaigns = (await this.getAll()).filter(c => c.status === 'sent');
    }

    const analytics = campaigns.map(c => this.calculateAnalytics(c));

    // Calculate averages
    const totalOpenRate = analytics.reduce((sum, a) => sum + a.openRate, 0);
    const totalClickRate = analytics.reduce((sum, a) => sum + a.clickRate, 0);
    const count = analytics.length || 1;

    // Find best and worst performing
    const sortedByOpenRate = [...analytics].sort((a, b) => b.openRate - a.openRate);

    return {
      campaigns: analytics,
      averageOpenRate: totalOpenRate / count,
      averageClickRate: totalClickRate / count,
      bestPerforming: sortedByOpenRate[0] || null,
      worstPerforming: sortedByOpenRate[sortedByOpenRate.length - 1] || null,
    };
  }

  /**
   * Get analytics by campaign type
   */
  static async getAnalyticsByType(campaignType: string): Promise<CampaignAnalytics[]> {
    const { data, error } = await supabase
      .from('newsletter_campaigns')
      .select('*')
      .eq('campaign_type', campaignType)
      .eq('status', 'sent')
      .order('sent_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch campaigns by type: ${error.message}`);
    }

    return (data || []).map(campaign => this.calculateAnalytics(campaign));
  }

  /**
   * Get failed sends for a campaign with pagination
   */
  static async getFailedSends(campaignId: string, limit: number = 100, offset: number = 0) {
    const { data, error } = await supabase
      .from('newsletter_campaign_failed_sends')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to fetch failed sends: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get successful sends for a campaign with pagination
   */
  static async getSuccessfulSends(campaignId: string, limit: number = 100, offset: number = 0) {
    const { data, error } = await supabase
      .from('newsletter_campaign_sends')
      .select('*')
      .eq('campaign_id', campaignId)
      .order('sent_at', { ascending: false })
      .range(offset, offset + limit - 1);

    if (error) {
      throw new Error(`Failed to fetch successful sends: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Get all newsletter subscribers
   */
  static async getAllSubscribers() {
    const { data, error } = await supabase
      .from('newsletter_subscribers')
      .select('*')
      .order('subscribed_at', { ascending: false });

    if (error) {
      throw new Error(`Failed to fetch subscribers: ${error.message}`);
    }

    return data || [];
  }

  /**
   * Calculate analytics metrics for a campaign
   */
  private static calculateAnalytics(campaign: NewsletterCampaign): CampaignAnalytics {
    const recipientCount = campaign.recipient_count || 0;
    const openCount = campaign.open_count || 0;
    const clickCount = campaign.click_count || 0;

    const openRate = recipientCount > 0 ? (openCount / recipientCount) * 100 : 0;
    const clickRate = recipientCount > 0 ? (clickCount / recipientCount) * 100 : 0;
    const clickToOpenRate = openCount > 0 ? (clickCount / openCount) * 100 : 0;

    return {
      campaignId: campaign.id,
      title: campaign.title,
      subject: campaign.subject,
      status: campaign.status,
      campaignType: campaign.campaign_type,
      sentAt: campaign.sent_at,
      recipientCount,
      openCount,
      clickCount,
      openRate: Math.round(openRate * 100) / 100,
      clickRate: Math.round(clickRate * 100) / 100,
      clickToOpenRate: Math.round(clickToOpenRate * 100) / 100,
    };
  }
}
